import { useEffect, useMemo } from 'react';

import { faRotate } from '@fortawesome/free-solid-svg-icons';
import { useFormikContext } from 'formik';
import { Form, Row, Stack } from 'react-bootstrap';

import IconButton from '@components/Button/IconButton';
import FormField from '@components/Form/FormField';
import PhoneField from '@components/Form/PhoneField';
import { OrganizationRole } from '@customTypes/clinician';
import { ICreateProviderForm } from '@customTypes/user';

const PROVIDER_PREFIX = 'provider';

const ACCOUNT_ROLE_OPTIONS = [
  {
    label: 'Admin',
    value: OrganizationRole.ADMIN,
  },
  {
    label: 'Ordering Provider†',
    value: OrganizationRole.CLINICIAN,
  },
  {
    label: 'Organization Delegate',
    value: OrganizationRole.DELEGATE,
  },
];

export default function AddUserForm() {
  const {
    isValid,
    errors,
    dirty,
    touched,
    values,
    isSubmitting,
    setFieldValue,
    handleChange,
    handleSubmit,
    setFieldTouched,
  } = useFormikContext<ICreateProviderForm>();

  const hasOrderingProviderRole = useMemo(
    () => values.organization_provider_roles.includes(OrganizationRole.CLINICIAN),
    [values.organization_provider_roles],
  );

  // Email is required unless ONLY Ordering Provider is selected
  const emailRequired = useMemo(() => {
    const roles = values.organization_provider_roles;
    const hasClinician = roles.includes(OrganizationRole.CLINICIAN);
    const onlyClinician = hasClinician && roles.length === 1;
    return !onlyClinician;
  }, [values.organization_provider_roles]);

  useEffect(() => {
    if (!isValid && touched.organization_provider_roles && dirty) {
      setFieldTouched(`${PROVIDER_PREFIX}.npi`, true);
      setFieldTouched(`${PROVIDER_PREFIX}.name.credentials`, true);
    }
  }, [setFieldTouched, isValid, touched.organization_provider_roles, dirty]);

  useEffect(() => {
    if (dirty && !values.organization_provider_roles.includes(OrganizationRole.CLINICIAN)) {
      setFieldValue(`${PROVIDER_PREFIX}.name.credentials`, undefined);
      setFieldValue(`${PROVIDER_PREFIX}.npi`, undefined);
    }
  }, [setFieldValue, values.organization_provider_roles, dirty]);

  return (
    <Form className='d-flex flex-column gap-4 mt-3' noValidate onSubmit={handleSubmit}>
      <fieldset id='fset_AccountRoles' className='fset'>
        <legend className='legend'>Account Roles</legend>
        <div className='ms-3'>
          <fieldset id='fset_SelectUserRoles' className='fset'>
            <legend className='legend user-roles-label fw-normal'>Select user roles</legend>
            {ACCOUNT_ROLE_OPTIONS.map(role => (
              <Form.Group className='ms-2' key={role.value}>
                <Form.Check
                  type='checkbox'
                  id={`${role.value}Check`}
                  name='organization_provider_roles'
                  label={role.label}
                  value={role.value}
                  checked={values.organization_provider_roles.includes(role.value)}
                  onChange={handleChange}
                />
              </Form.Group>
            ))}
          </fieldset>
          <p className='mb-0 form-text'>†An ordering provider is a physician of record with a verified NPI</p>
          {!!errors.organization_provider_roles && touched.organization_provider_roles && (
            <small role='alert' className='invalid-feedback d-block'>
              {errors.organization_provider_roles as string}
            </small>
          )}
        </div>
      </fieldset>

      <fieldset id='fset_Profile' className='fset' aria-describedby='fset_ProfileDesc'>
        <legend className='legend'>New User Profile</legend>
        <Stack className='gap-12 px-3'>
          <p id='fset_ProfileDesc' className='text-body-secondary mb-0'>
            Set up an account profile for a new user.
          </p>
          <Row>
            <FormField cols={4} prefix={PROVIDER_PREFIX} name='name.first' label='First name' required />
            <FormField cols={4} prefix={PROVIDER_PREFIX} name='name.middle' label='Middle name or initial' />
            <FormField cols={4} prefix={PROVIDER_PREFIX} name='name.last' label='Last name' required />
          </Row>

          <Row>
            <FormField cols={4} prefix={PROVIDER_PREFIX} name='email' type='email' required={emailRequired} />
            <PhoneField cols={4} prefix={PROVIDER_PREFIX} name='phone' />
            <PhoneField cols={4} prefix={PROVIDER_PREFIX} name='fax' />
          </Row>
        </Stack>
      </fieldset>

      <fieldset id='fset_Npi' className='fset' aria-describedby='fset_NpiDesc'>
        <legend className='legend'>NPI and Credentials</legend>
        <Stack className='gap-12 px-3'>
          <p id='fset_NpiDesc' className='text-body-secondary mb-0'>
            Required for ordering providers
          </p>
          <Row>
            <FormField
              cols={4}
              prefix={PROVIDER_PREFIX}
              name='npi'
              disabled={!hasOrderingProviderRole}
              required={hasOrderingProviderRole}
              label='NPI'
            />
            <FormField
              cols={4}
              prefix={PROVIDER_PREFIX}
              name='name.credentials'
              label='Credentials'
              disabled={!hasOrderingProviderRole}
              required={hasOrderingProviderRole}
              helperText='e.g. MD, RNP, etc'
            />
          </Row>
        </Stack>
      </fieldset>

      <div className='d-flex justify-content-start user-form-bottom'>
        <IconButton
          startIcon={isSubmitting ? faRotate : undefined}
          type='submit'
          data-testid='submit-user'
          variant='primary'
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </IconButton>
      </div>
    </Form>
  );
}
